/**
 * User Store
 *
 * Manages user session state including authentication, property selection,
 * and council information.
 */

import { sessionStore, STORAGE_KEYS } from '../utils/storage.js';

/**
 * UserStore class - manages user/session state
 */
class UserStore {
    constructor() {
        // Authentication state (tokens handled server-side by proxy)
        this._isAuthenticated = false;
        this._entraOrgId = null;

        // Property/Address state
        this._address = null;
        this._propId = null;
        this._gurasId = null;
        this._zone = null;
        this._zoneDescription = null;
        this._lotSize = null;
        this._lotWidth = null;
        this._floorSpaceRatio = null;
        this._mineSubsidenceAffects = null;
        this._minLotSize = null;
        this._maxBuildingHeight = null;
        this._activePrecinct = null;
        this._propertyPrecinct = null;
        this._isBattleAxeLot = false;
        this._isCornerLot = false;

        // Site Council state (from URL parameter - represents the council whose website embeds this chatbot)
        // NOTE: This should NOT be overwritten by address lookup results.
        // Address lookup returns the council where a property is located, which may differ from the site council.
        this._councilName = null;
        this._councilWebsiteUrl = null;

        // Property restrictions
        this._bushFireZone = false;
        this._floodZone = false;
        this._heritageZone = false;

        // Project management (v3 API pattern)
        this._currentProject = null;
        this._projects = [];
        this._tenantId = null;
        this._workflowId = null;

        // Subscribers for reactive updates
        this._subscribers = new Set();
    }

    /**
     * Subscribe to state changes
     * @param {Function} callback - Called when state changes
     * @returns {Function} - Unsubscribe function
     */
    subscribe(callback) {
        this._subscribers.add(callback);
        callback(this.getState());
        return () => this._subscribers.delete(callback);
    }

    /**
     * Notify all subscribers of state changes
     */
    _notify() {
        const state = this.getState();
        this._subscribers.forEach((callback) => callback(state));
    }

    /**
     * Get current state snapshot
     * @returns {Object} - Current state
     */
    getState() {
        return {
            isAuthenticated: this._isAuthenticated,
            entraOrgId: this._entraOrgId,
            address: this._address,
            propId: this._propId,
            gurasId: this._gurasId,
            zone: this._zone,
            zoneDescription: this._zoneDescription,
            lotSize: this._lotSize,
            lotWidth: this._lotWidth,
            floorSpaceRatio: this._floorSpaceRatio,
            mineSubsidenceAffects: this._mineSubsidenceAffects,
            minLotSize: this._minLotSize,
            maxBuildingHeight: this._maxBuildingHeight,
            activePrecinct: this._activePrecinct,
            propertyPrecinct: this._propertyPrecinct,
            isBattleAxeLot: this._isBattleAxeLot,
            isCornerLot: this._isCornerLot,
            councilName: this._councilName,
            councilWebsiteUrl: this._councilWebsiteUrl,
            bushFireZone: this._bushFireZone,
            floodZone: this._floodZone,
            heritageZone: this._heritageZone,
            currentProject: this._currentProject,
            projects: [...this._projects],
            tenantId: this._tenantId,
            workflowId: this._workflowId
        };
    }

    /**
     * Load state from sessionStorage
     */
    loadFromStorage() {
        // Load auth state (tokens are now handled server-side by proxy)
        this._entraOrgId = sessionStore.get(STORAGE_KEYS.ENTRA_ORG_ID);
        this._isAuthenticated = !!this._entraOrgId;

        // Load property state
        this._address = sessionStore.get(STORAGE_KEYS.ADDRESS);
        this._propId = sessionStore.get(STORAGE_KEYS.PROP_ID);
        this._gurasId = sessionStore.get(STORAGE_KEYS.GURAS_ID);
        this._zone = sessionStore.get(STORAGE_KEYS.ZONE);
        this._zoneDescription = sessionStore.get(STORAGE_KEYS.ZONE_DESCRIPTION);
        this._lotSize = sessionStore.get(STORAGE_KEYS.LOT_SIZE);
        this._lotWidth = sessionStore.get(STORAGE_KEYS.LOT_WIDTH);
        this._floorSpaceRatio = sessionStore.get(STORAGE_KEYS.FLOOR_SPACE_RATIO);
        this._mineSubsidenceAffects = sessionStore.getJSON(STORAGE_KEYS.MINE_SUBSIDENCE_AFFECTS);
        this._minLotSize = sessionStore.getJSON(STORAGE_KEYS.MIN_LOT_SIZE);
        this._maxBuildingHeight = sessionStore.getJSON(STORAGE_KEYS.MAX_BUILDING_HEIGHT);
        this._activePrecinct = sessionStore.get(STORAGE_KEYS.ACTIVE_PRECINCT);
        this._propertyPrecinct = sessionStore.getJSON(STORAGE_KEYS.PROPERTY_PRECINCT);
        this._isBattleAxeLot = sessionStore.getJSON(STORAGE_KEYS.IS_BATTLE_AXE_LOT) || false;
        this._isCornerLot = sessionStore.getJSON(STORAGE_KEYS.IS_CORNER_LOT) || false;

        // Load council state - first check URL params, then session storage
        this._councilName = this._getCouncilFromUrl() || sessionStore.get(STORAGE_KEYS.COUNCIL_NAME);
        this._councilWebsiteUrl = sessionStore.get(STORAGE_KEYS.COUNCIL_WEBSITE_URL);

        // Persist council from URL to session storage for consistency
        if (this._councilName) {
            sessionStore.set(STORAGE_KEYS.COUNCIL_NAME, this._councilName);
        }

        // Load project data
        this.loadProjectFromStorage();

        this._notify();
    }

    /**
     * Get council name from URL parameters
     * Supports both widget.js injection and manual iframe embedding
     * @returns {string|null} - Council name from URL or null
     */
    _getCouncilFromUrl() {
        // Check window.DAISY_CONFIG first (set by eaidev.html)
        if (typeof window !== 'undefined' && window.DAISY_CONFIG?.council) {
            return window.DAISY_CONFIG.council;
        }

        // Check URL parameters before persisted storage so the current embed
        // context wins over a previous council saved in this browser session.
        if (typeof window !== 'undefined' && window.location) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlCouncil = urlParams.get('Council') || urlParams.get('council');
            if (urlCouncil) return urlCouncil;
        }

        // Check session storage only when the current embed context is absent.
        if (typeof sessionStorage !== 'undefined') {
            const stored = sessionStorage.getItem('daisy_council');
            if (stored) return stored;
        }

        return null;
    }

    // ==================== Authentication ====================

    /**
     * Set entraOrgId for tenant context (tokens handled server-side by proxy)
     * @param {string} entraOrgId - Entra organization ID
     */
    setEntraOrgId(entraOrgId) {
        this._entraOrgId = entraOrgId;
        this._isAuthenticated = !!entraOrgId;

        if (entraOrgId) {
            sessionStore.set(STORAGE_KEYS.ENTRA_ORG_ID, entraOrgId);
        }

        this._notify();
    }

    /**
     * Clear authentication state
     */
    clearAuth() {
        this._entraOrgId = null;
        this._isAuthenticated = false;

        sessionStore.remove(STORAGE_KEYS.ENTRA_ORG_ID);

        this._notify();
    }

    /**
     * Check if auth session is valid
     * @returns {boolean} - True if entraOrgId is present
     */
    isAuthValid() {
        return this._isAuthenticated && !!this._entraOrgId;
    }

    // ==================== Property/Address ====================

    /**
     * Set property information
     * @param {Object} propertyData - Property data
     */
    setProperty(propertyData) {
        const {
            address,
            propId,
            gurasId,
            zone,
            zoneDescription,
            lotSize,
            lotWidth,
            floorSpaceRatio,
            mineSubsidenceAffects,
            minLotSize,
            maxBuildingHeight,
            activePrecinct,
            propertyPrecinct,
            isBattleAxeLot,
            isCornerLot,
            bushFireZone,
            floodZone,
            heritageZone
        } = propertyData;

        if (address !== undefined) {
            this._address = address;
            sessionStore.set(STORAGE_KEYS.ADDRESS, address);
        }
        if (propId !== undefined) {
            this._propId = propId;
            sessionStore.set(STORAGE_KEYS.PROP_ID, propId);
        }
        if (gurasId !== undefined) {
            this._gurasId = gurasId;
            sessionStore.set(STORAGE_KEYS.GURAS_ID, gurasId);
        }
        if (zone !== undefined) {
            this._zone = zone;
            sessionStore.set(STORAGE_KEYS.ZONE, zone);
        }
        if (zoneDescription !== undefined) {
            this._zoneDescription = zoneDescription;
            sessionStore.set(STORAGE_KEYS.ZONE_DESCRIPTION, zoneDescription);
        }
        if (lotSize !== undefined) {
            this._lotSize = lotSize;
            sessionStore.set(STORAGE_KEYS.LOT_SIZE, lotSize);
        }
        if (lotWidth !== undefined) {
            this._lotWidth = lotWidth;
            sessionStore.set(STORAGE_KEYS.LOT_WIDTH, lotWidth);
        }
        if (floorSpaceRatio !== undefined) {
            this._floorSpaceRatio = floorSpaceRatio;
            sessionStore.set(STORAGE_KEYS.FLOOR_SPACE_RATIO, floorSpaceRatio);
        }
        if (mineSubsidenceAffects !== undefined) {
            this._mineSubsidenceAffects = mineSubsidenceAffects;
            sessionStore.setJSON(STORAGE_KEYS.MINE_SUBSIDENCE_AFFECTS, mineSubsidenceAffects);
        }
        if (minLotSize !== undefined) {
            this._minLotSize = minLotSize;
            sessionStore.setJSON(STORAGE_KEYS.MIN_LOT_SIZE, minLotSize);
        }
        if (maxBuildingHeight !== undefined) {
            this._maxBuildingHeight = maxBuildingHeight;
            sessionStore.setJSON(STORAGE_KEYS.MAX_BUILDING_HEIGHT, maxBuildingHeight);
        }
        if (activePrecinct !== undefined) {
            this._activePrecinct = activePrecinct;
            sessionStore.set(STORAGE_KEYS.ACTIVE_PRECINCT, activePrecinct);
        }
        if (propertyPrecinct !== undefined) {
            this._propertyPrecinct = propertyPrecinct;
            if (propertyPrecinct) {
                sessionStore.setJSON(STORAGE_KEYS.PROPERTY_PRECINCT, propertyPrecinct);
            } else {
                sessionStore.remove(STORAGE_KEYS.PROPERTY_PRECINCT);
            }
        }
        if (isBattleAxeLot !== undefined) {
            this._isBattleAxeLot = isBattleAxeLot;
            sessionStore.setJSON(STORAGE_KEYS.IS_BATTLE_AXE_LOT, isBattleAxeLot);
        }
        if (isCornerLot !== undefined) {
            this._isCornerLot = isCornerLot;
            sessionStore.setJSON(STORAGE_KEYS.IS_CORNER_LOT, isCornerLot);
        }
        if (bushFireZone !== undefined) {
            this._bushFireZone = bushFireZone;
        }
        if (floodZone !== undefined) {
            this._floodZone = floodZone;
        }
        if (heritageZone !== undefined) {
            this._heritageZone = heritageZone;
        }

        this._notify();
    }

    /**
     * Clear property information
     */
    clearProperty() {
        this._address = null;
        this._propId = null;
        this._gurasId = null;
        this._zone = null;
        this._zoneDescription = null;
        this._lotSize = null;
        this._lotWidth = null;
        this._floorSpaceRatio = null;
        this._mineSubsidenceAffects = null;
        this._minLotSize = null;
        this._maxBuildingHeight = null;
        this._activePrecinct = null;
        this._propertyPrecinct = null;
        this._isBattleAxeLot = false;
        this._isCornerLot = false;
        this._bushFireZone = false;
        this._floodZone = false;
        this._heritageZone = false;

        sessionStore.remove(STORAGE_KEYS.ADDRESS);
        sessionStore.remove(STORAGE_KEYS.PROP_ID);
        sessionStore.remove(STORAGE_KEYS.GURAS_ID);
        sessionStore.remove(STORAGE_KEYS.ZONE);
        sessionStore.remove(STORAGE_KEYS.ZONE_DESCRIPTION);
        sessionStore.remove(STORAGE_KEYS.LOT_SIZE);
        sessionStore.remove(STORAGE_KEYS.LOT_WIDTH);
        sessionStore.remove(STORAGE_KEYS.FLOOR_SPACE_RATIO);
        sessionStore.remove(STORAGE_KEYS.MINE_SUBSIDENCE_AFFECTS);
        sessionStore.remove(STORAGE_KEYS.MIN_LOT_SIZE);
        sessionStore.remove(STORAGE_KEYS.MAX_BUILDING_HEIGHT);
        sessionStore.remove(STORAGE_KEYS.ACTIVE_PRECINCT);
        sessionStore.remove(STORAGE_KEYS.PROPERTY_PRECINCT);
        sessionStore.remove(STORAGE_KEYS.IS_BATTLE_AXE_LOT);
        sessionStore.remove(STORAGE_KEYS.IS_CORNER_LOT);

        this._notify();
    }

    // ==================== Site Council ====================

    // ==================== Project Management ====================

    /**
     * Set the current project
     * @param {Object} project - Project object
     */
    setCurrentProject(project) {
        this._currentProject = project;
        if (project) {
            sessionStore.setJSON('currentProject', project);
        } else {
            sessionStore.remove('currentProject');
        }
        this._notify();
    }

    /**
     * Update a property on the current project
     * Supports nested paths like 'workflow.next_action'
     * @param {string} path - Property path (dot-separated for nested)
     * @param {any} value - Value to set
     */
    updateProjectProperty(path, value) {
        if (!this._currentProject) return;

        const keys = path.split('.');
        let obj = this._currentProject;

        // Navigate to the parent of the target property
        for (let i = 0; i < keys.length - 1; i++) {
            if (!obj[keys[i]]) obj[keys[i]] = {};
            obj = obj[keys[i]];
        }

        // Set the value
        obj[keys[keys.length - 1]] = value;

        // Persist and notify
        sessionStore.setJSON('currentProject', this._currentProject);
        this._notify();
    }

    /**
     * Get the current project
     * @returns {Object|null} - Current project or null
     */
    getCurrentProject() {
        return this._currentProject;
    }

    /**
     * Load project data from storage
     */
    loadProjectFromStorage() {
        this._currentProject = sessionStore.getJSON('currentProject');
        this._projects = sessionStore.getJSON('projects') || [];
        this._tenantId = sessionStore.get('tenantId');
        this._workflowId = sessionStore.get('workflowId');
    }

    // ==================== Getters ====================

    get entraOrgId() {
        return this._entraOrgId;
    }

    get address() {
        return this._address;
    }

    get propId() {
        return this._propId;
    }

    get councilName() {
        return this._councilName;
    }
}

// Export singleton instance
export const userStore = new UserStore();
